import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bookmark, Rocket, Download, Trash2, Loader2, Link2, Users, X } from "lucide-react";
import PresetSchedule from "@/components/PresetSchedule";
import PresetGallery from "@/components/PresetGallery";

export default function CrawlPresets({ buildConfig, applyConfig, canSave, onLaunched }) {
  const [presets, setPresets] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState("");
  const [incoming, setIncoming] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = () => api.listPresets().then((d) => setPresets(d.presets)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("preset");
    if (!token) return;
    api.getSharedPreset(token)
      .then((d) => setIncoming({ ...d, token }))
      .catch((e) => toast.error(e?.response?.data?.detail || "That shared preset link is not valid"));
  }, []);

  const clearUrl = () => window.history.replaceState({}, "", window.location.pathname);

  const acceptIncoming = async () => {
    setImporting(true);
    try {
      const p = await api.importSharedPreset(incoming.token);
      toast.success(`"${p.name}" added to your presets`);
      setIncoming(null);
      clearUrl();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import preset");
    } finally { setImporting(false); }
  };

  const share = async (p) => {
    try {
      const r = await api.sharePreset(p.id);
      await navigator.clipboard.writeText(r.share_link).catch(() => {});
      toast.success("Share link copied — anyone with an account can import it", { description: r.share_link });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    }
  };

  const save = async () => {
    if (!canSave) { toast.error("Add a keyword, seed URL or sitemap before saving a preset"); return; }
    setSaving(true);
    try {
      const cfg = buildConfig();
      const p = await api.savePreset(name.trim() || cfg.name, cfg);
      setName("");
      toast.success(`Preset "${p.name}" saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preset");
    } finally { setSaving(false); }
  };

  const launch = async (p) => {
    setLaunching(p.id);
    try {
      const r = await api.launchPreset(p.id, true);
      toast.success(`"${r.crawl.name}" launched from preset`);
      onLaunched?.(r.crawl);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not launch preset");
    } finally { setLaunching(""); }
  };

  const remove = async (p) => {
    try { await api.deletePreset(p.id); load(); toast.success("Preset deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="rounded-md border border-border bg-[#0E1420]/60 p-3.5 space-y-3" data-testid="crawl-presets">
      <div className="flex items-center gap-2">
        <Bookmark className="h-3.5 w-3.5 text-violet-400" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Presets</span>
        <span className="ml-auto font-mono text-[10px] text-slate-600">one-click reruns</span>
      </div>

      <div className="flex gap-2">
        <Input data-testid="preset-name-input" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="name this setup" className="bg-[#0B0F18] border-border font-mono text-xs h-9" />
        <Button data-testid="save-preset-button" onClick={save} disabled={saving}
          className="h-9 shrink-0 bg-violet-500/90 hover:bg-violet-400 text-slate-950 font-semibold text-xs">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save setup"}
        </Button>
      </div>

      {incoming && (
        <div data-testid="preset-import-card"
          className="rounded border border-violet-500/50 bg-violet-500/10 px-3 py-2.5 space-y-2">
          <div className="flex items-start gap-2">
            <Users className="h-3.5 w-3.5 text-violet-300 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs text-violet-100 truncate">{incoming.name}</p>
              <p className="font-mono text-[10px] text-violet-300/70">
                shared by {incoming.shared_by} · {(incoming.config.keywords || []).length} kw ·{" "}
                {(incoming.config.seed_urls || []).length} seed · {incoming.config.max_pages} pgs
              </p>
            </div>
            <button data-testid="dismiss-preset-import" onClick={() => { setIncoming(null); clearUrl(); }}
              className="text-violet-300/60 hover:text-violet-100"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" data-testid="import-preset-button" onClick={acceptIncoming}
              disabled={importing || incoming.mine}
              className="h-7 bg-violet-500 hover:bg-violet-400 text-slate-950 font-semibold text-xs disabled:opacity-40">
              {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Import to my presets"}
            </Button>
            <Button size="sm" variant="ghost" data-testid="preview-shared-preset"
              onClick={() => { applyConfig(incoming.config); toast.info("Loaded the shared setup into the form"); }}
              className="h-7 text-violet-200 hover:text-violet-100 text-xs">Just load it once</Button>
          </div>
          {incoming.mine && (
            <p className="font-mono text-[10px] text-violet-300/70">this is your own preset — nothing to import</p>
          )}
        </div>
      )}

      {presets.length === 0 ? (
        <p className="font-mono text-[10px] text-slate-600" data-testid="no-presets-note">
          save the current form as a template, then launch identical jobs in one click
        </p>
      ) : (
        <div className="space-y-1.5" data-testid="preset-list">
          {presets.map((p) => (
            <div key={p.id} data-testid={`preset-row-${p.id}`}
              className="rounded border border-border bg-[#0B0F18] px-2.5 py-2">
              <div className="flex items-center gap-1.5">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-xs text-slate-200 truncate">{p.name}</p>
                <p className="font-mono text-[9px] text-slate-600 truncate">
                  {(p.config.keywords || []).length ? `${p.config.keywords.length} kw` : ""}
                  {(p.config.seed_urls || []).length ? ` · ${p.config.seed_urls.length} seed` : ""}
                  {(p.config.sitemap_urls || []).length ? ` · sitemap` : ""}
                  {` · ${p.config.max_pages} pgs · ${Math.round((p.config.time_limit_seconds || 0) / 60)}m`}
                  {p.used_count ? ` · used ${p.used_count}x` : ""}
                  {p.share_token ? " · shared" : ""}
                </p>
              </div>
              <Button size="sm" variant="ghost" data-testid={`launch-preset-${p.id}`} onClick={() => launch(p)}
                disabled={launching === p.id} title="Create and start a job from this preset"
                className="h-7 px-2 text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10">
                {launching === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="ghost" data-testid={`share-preset-${p.id}`} onClick={() => share(p)}
                title="Copy a link teammates can import"
                className={`h-7 px-2 hover:text-violet-200 ${p.share_token ? "text-violet-300" : "text-slate-400"}`}>
                <Link2 className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" data-testid={`load-preset-${p.id}`}
                onClick={() => { applyConfig(p.config); toast.info(`Loaded "${p.name}" into the form`); }}
                title="Load into the form"
                className="h-7 px-2 text-slate-400 hover:text-slate-200"><Download className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" data-testid={`delete-preset-${p.id}`} onClick={() => remove(p)}
                title="Delete preset"
                className="h-7 px-2 text-slate-600 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              <PresetSchedule preset={p} onSaved={load} />
            </div>
          ))}
        </div>
      )}

      <PresetGallery applyConfig={applyConfig} onImported={load} />
    </div>
  );
}
